<script setup lang="ts">
import type { Schemas } from '#shopware'
import { MiscLink } from '#components'

const props = defineProps<{
    content: Schemas['CmsElementTiles']
}>()

const tiles = computed(() => props.content.config?.tilesList?.value ?? [])
const isLayout3Cols = computed(() => props.content.config?.displayThreeCols?.value)
const isColorThemePrimary = computed(() => props.content.config?.colorTheme?.value === 'primary')
const isColorThemeWhite = computed(() => props.content.config?.colorTheme?.value === 'white')
const mobileTileInRow = computed(() => props.content.config?.mobileTileInRow?.value)
const lineHeight = computed(() => props.content.config?.lineHeight?.value || null)
const titleBodySpacing = computed(() => props.content.config?.titleBodySpacing?.value || null)
const tileGap = computed(() => props.content.config?.tileGap?.value || null)

const _experiencePngIcons = 'expert personalized planning all-round-carefree'

function isTileIconPng(iconName: string) {
    return iconName === 'expert' || iconName === 'personalized' || iconName === 'planning' || iconName === 'all-round-carefree'
}

function getListItems(text: string) {
    return text.split(/\r?\n/)
}
</script>

<template>
    <div
        class="slot-tiles 2xl:mx-auto flex flex-col sm:flex-row sm:flex-wrap"
        :class="{ 'gap-4 md:gap-6': !tileGap }"
        :style="tileGap ? { gap: `${tileGap}px` } : {}"
    >
        <div
            v-for="(tile, index) in tiles"
            :key="index"
            class="tile-content flex grow shrink-0 basis-0 sm:basis-[calc(50%-32px)] md:basis-[calc(50%-48px)] aspect-auto p-4 text-left rounded-xs"
            :class="[
                {
                    'xl:basis-[calc(25%-48px)]': !isLayout3Cols,
                    'xl:basis-[calc(33.33%-48px)]': isLayout3Cols,
                    'bg-white': isColorThemeWhite,
                },
                isColorThemePrimary ? 'bg-primary text-base-100' : (!isColorThemeWhite ? 'bg-base-300 text-base-content' : 'text-base-content'),
            ]"
        >
            <component
                :is="tile.tileTarget ? MiscLink : 'div'"
                :to="tile.tileTarget || undefined"
            >
                <div
                    :class="[
                        mobileTileInRow ? 'flex md:flex-col gap-4 items-start' : 'flex flex-col justify-between items-left',
                        { 'primary': isColorThemePrimary, 'white': isColorThemeWhite }
                    ]"
                >
                    <template v-if="tile.iconName.value && isTileIconPng(tile.iconName.value)">
                        <img
                            :src="`/images/experience-images/${tile.iconName.value}.png`"
                            class="max-w-[104px] md:max-w-[160px]"
                            :alt="tile.iconName.value"
                            fetchpriority="low"
                        >
                    </template>
                    <template v-else>
                        <MiscIcon
                            v-if="tile.iconName.value"
                            :name="tile.iconName.value"
                            size="2xl"
                        />
                    </template>

                    <div>
                        <div
                            v-if="tile.subtitle"
                            class="text-sm font-normal"
                        >
                            {{ tile.subtitle }}
                        </div>
                        <div
                            class="text-xl"
                            :class="[
                                tile.titleSemiBold ? 'font-semibold' : 'font-normal',
                                { 'pb-6': !titleBodySpacing }
                            ]"
                            :style="titleBodySpacing ? { paddingBottom: `${titleBodySpacing}px` } : {}"
                        >
                            {{ tile.title }}
                        </div>
                        <ul
                            v-if="tile.showTextAsList"
                            class="list-disc text-sm font-normal px-4"
                            :style="lineHeight ? { lineHeight } : {}"
                        >
                            <li
                                v-for="(line, i) in getListItems(tile.text)"
                                :key="i"
                            >
                                {{ line }}
                            </li>
                        </ul>
                        <div
                            v-else
                            class="text-sm font-normal"
                            :style="lineHeight ? { lineHeight } : {}"
                        >
                            {{ tile.text }}
                        </div>
                    </div>
                </div>
            </component>
        </div>
    </div>
</template>
